import {
  ChevronDownIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import axios from "axios";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReportTemplate } from "../../db/schema";

interface TemplateSelectorProps {
  selectedTemplateId: string | null;
  onSelect: (templateId: string | null) => void;
  className?: string;
  compact?: boolean;
  /** Override classes on the trigger button */
  triggerClassName?: string;
  /** Pre-select the officer's default template on mount (off for Add to Report) */
  autoSelectDefault?: boolean;
}

export default function TemplateSelector({
  selectedTemplateId,
  onSelect,
  className,
  compact = false,
  triggerClassName,
  autoSelectDefault = true,
}: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const showAsDefault = (t: ReportTemplate) => t.id === defaultTemplateId;

  // On mount: fetch templates and auto-select default if none selected
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const { data } = await axios.get("/api/report-templates/templates");
        setTemplates(data.templates);
        const dtId = data.defaultTemplateId ?? null;
        setDefaultTemplateId(dtId);

        if (autoSelectDefault && !selectedTemplateId && dtId) {
          const defaultTemplate = data.templates.find(
            (t: ReportTemplate) => t.id === dtId
          );
          if (defaultTemplate) {
            onSelect(defaultTemplate.id);
          }
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownMenuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  if (isLoading || templates.length === 0) {
    return null;
  }

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  return (
    <div className={cn("relative", className)}>
      {!compact && (
        <label className="block text-xs font-medium text-slate-600 dark:text-base-content/70 mb-1.5">
          Report Template
        </label>
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom - 8;
            setDropdownStyle({
              position: "fixed",
              top: rect.bottom,
              left: rect.left,
              width: rect.width,
              maxHeight: Math.max(spaceBelow, 120),
            });
          }
          setIsOpen(!isOpen);
        }}
        className={triggerClassName ?? cn(
          "w-full flex items-center justify-between gap-2 rounded-lg border transition-colors text-left",
          compact ? "px-2.5 py-1.5" : "px-3 py-2",
          isOpen
            ? "border-blue-400 bg-blue-50/30 dark:bg-blue-900/30"
            : "border-base-300 bg-base-200 hover:border-slate-300 dark:hover:border-base-content/30"
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <DocumentTextIcon
            className={cn(
              "shrink-0",
              compact ? "w-3.5 h-3.5" : "w-4 h-4",
              selectedTemplate ? "text-blue-500 dark:text-blue-300" : "text-slate-400 dark:text-base-content/40"
            )}
          />
          <span
            className={cn(
              "truncate",
              compact ? "text-xs" : "text-sm",
              selectedTemplate
                ? "text-slate-700 dark:text-base-content/80 font-medium"
                : "text-slate-400 dark:text-base-content/40"
            )}
          >
            {selectedTemplate
              ? selectedTemplate.name
              : "No template (only use preferences)"}
          </span>
          {selectedTemplate?.user_id === null && (
            <span className="text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-300 px-1 py-0.5 rounded shrink-0">Dept</span>
          )}
          {selectedTemplate && showAsDefault(selectedTemplate) && (
            <StarIconSolid className="w-3 h-3 text-amber-400 shrink-0" />
          )}
        </div>
        <ChevronDownIcon
          className={cn(
            "w-3.5 h-3.5 text-slate-400 dark:text-base-content/40 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown - portaled to closest dialog (top layer) or body */}
      {isOpen && createPortal(
        <div
          ref={dropdownMenuRef}
          style={dropdownStyle}
          className="fixed z-[9999] bg-base-100 rounded-lg shadow-lg border border-base-300 overflow-y-auto"
        >
          {/* "None" option */}
          <button
            onClick={() => {
              onSelect(null);
              setIsOpen(false);
            }}
            className={cn(
              "w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2",
              !selectedTemplateId
                ? "bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                : "hover:bg-base-200 text-slate-500 dark:text-base-content/60"
            )}
          >
            <span className="w-4 h-4 shrink-0" />
            <span>No template (only use preferences)</span>
          </button>

          {/* Template options - effective default first */}
          {[...templates].sort((a, b) => (showAsDefault(b) ? 1 : 0) - (showAsDefault(a) ? 1 : 0)).map((template) => (
            <button
              key={template.id}
              onClick={() => {
                onSelect(template.id);
                setIsOpen(false);
              }}
              className={cn(
                "w-full text-left px-3 py-2 transition-colors",
                selectedTemplateId === template.id
                  ? "bg-blue-50 dark:bg-blue-900/40"
                  : "hover:bg-base-200"
              )}
            >
              <div className="flex items-center gap-2">
                <DocumentTextIcon
                  className={cn(
                    "w-4 h-4 shrink-0",
                    selectedTemplateId === template.id
                      ? "text-blue-500 dark:text-blue-300"
                      : "text-slate-400 dark:text-base-content/40"
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "text-sm font-medium truncate",
                        selectedTemplateId === template.id
                          ? "text-blue-700 dark:text-blue-300"
                          : "text-slate-700 dark:text-base-content/80"
                      )}
                    >
                      {template.name}
                    </span>
                    {template.user_id === null && (
                      <span className="text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-300 px-1 py-0.5 rounded shrink-0">Dept</span>
                    )}
                    {showAsDefault(template) && (
                      <StarIconSolid className="w-3 h-3 text-amber-400 shrink-0" />
                    )}
                  </div>
                  {template.description && (
                    <p className="text-xs text-slate-400 dark:text-base-content/40 truncate">
                      {template.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>,
        triggerRef.current?.closest("dialog") || document.body
      )}
    </div>
  );
}
